/**
 * The Vaktplan's paper rows (#49, #200): «Ledig» first, then each person, under their department when
 * the week has sections (#199). A person's day lists the shifts, then in italics the absence («Ferie»,
 * «(søkt)» while it waits, so the paper does not promise a day that is not granted) or «Fri» on a day
 * they cannot work; the hours per day by the Sum row's rule fill Timer. Pure; tested in paper.test.ts.
 */
import type { ApiShift } from '@/api/data';
import { T } from '@/i18n';
import { plannedHoursOn } from '@/rules';
import { fullName } from '@/ui/format';
import { type PrintRow, shiftLine } from '@/ui/print';
import type { Section } from './departments';
import { type Row, typeLabel } from './model';

const line = (s: ApiShift) => shiftLine(s, typeLabel(s.shiftType));

export function paperRows(
  days: string[],
  rows: Row[],
  sections: Section[] | null,
  open: Map<string, ApiShift[]>,
  shifts: ApiShift[],
): PrintRow[] {
  const person = (r: Row): PrintRow => {
    const mine = shifts.filter((s) => s.user_id === r.user.id);
    return {
      key: r.user.id,
      name: fullName(r.user),
      cells: new Map(days.map((d) => [d, (r.cells.get(d)?.shifts ?? []).map(line)])),
      notes: new Map(
        days.map((d) => {
          const c = r.cells.get(d);
          const a = c?.absence;
          if (a && a.status !== 'declined')
            return [
              d,
              [`${a.absence_type?.name ?? T('Fravær')}${a.status === 'pending' ? ` (${T('søkt')})` : ''}`],
            ];
          return [d, c?.avail?.kind === 'unavail' && !c.shifts.length ? [T('Fri')] : []];
        }),
      ),
      hours: new Map(days.map((d) => [d, mine.reduce((a, s) => a + plannedHoursOn(s, d), 0)])),
    };
  };
  return [
    { key: 'open', name: T('Ledig'), cells: new Map(days.map((d) => [d, (open.get(d) ?? []).map(line)])) },
    ...(sections
      ? sections.flatMap((s) => [
          { key: `dep-${s.key}`, name: s.name, cells: new Map(), heading: true },
          ...s.rows.map(person),
        ])
      : rows.map(person)),
  ];
}
